/**
 * Chart Theme — Consistent color palette and styling for all charts.
 *
 * Uses CSS custom properties from our design tokens so charts
 * automatically adapt to light/dark mode.
 *
 * Usage:
 * ```tsx
 * import { chartColors, chartTheme } from '@yesbos/ui';
 * ```
 */

// ─── Color Palette ──────────────────────────────────────────────────
// 12 distinct colors for chart series. Ordered for maximum contrast
// when shown side-by-side.

export const chartColors = [
  'var(--color-chart-1, #2563eb)', // blue
  'var(--color-chart-2, #16a34a)', // green
  'var(--color-chart-3, #ea580c)', // orange
  'var(--color-chart-4, #9333ea)', // purple
  'var(--color-chart-5, #dc2626)', // red
  'var(--color-chart-6, #0891b2)', // cyan
  'var(--color-chart-7, #ca8a04)', // yellow
  'var(--color-chart-8, #be185d)', // pink
  'var(--color-chart-9, #4f46e5)', // indigo
  'var(--color-chart-10, #059669)', // emerald
  'var(--color-chart-11, #d97706)', // amber
  'var(--color-chart-12, #7c3aed)', // violet
] as const;

/**
 * Get a chart color by index (wraps around if index > palette size).
 */
export function getChartColor(index: number): string {
  return chartColors[index % chartColors.length];
}

// ─── Chart Theme Config ──────────────────────────────────────────────

export interface ChartThemeConfig {
  /** Default chart colors */
  colors: readonly string[];
  /** Grid line color */
  gridColor: string;
  /** Axis tick color */
  tickColor: string;
  /** Tooltip background */
  tooltipBg: string;
  /** Tooltip border */
  tooltipBorder: string;
  /** Tooltip text color */
  tooltipText: string;
  /** Font family */
  fontFamily: string;
  /** Font size for labels */
  fontSize: number;
  /** Stroke width for lines */
  strokeWidth: number;
  /** Dot radius */
  dotRadius: number;
  /** Active dot radius */
  activeDotRadius: number;
  /** Animation duration in ms */
  animationDuration: number;
  /** Cursor line style */
  cursorStroke: string;
}

export const chartTheme: ChartThemeConfig = {
  colors: chartColors,
  gridColor: 'var(--color-border, #e5e7eb)',
  tickColor: 'var(--color-muted-foreground, #6b7280)',
  tooltipBg: 'var(--color-popover, #ffffff)',
  tooltipBorder: 'var(--color-border, #e5e7eb)',
  tooltipText: 'var(--color-popover-foreground, #09090b)',
  fontFamily: 'inherit',
  fontSize: 12,
  strokeWidth: 2,
  dotRadius: 3,
  activeDotRadius: 5,
  animationDuration: 300,
  cursorStroke: 'var(--color-muted-foreground, #6b7280)',
};

// ─── Recharts Shared Props ──────────────────────────────────────────
// Pre-configured props objects for Recharts components, ensuring
// consistent styling across all charts.

export const defaultCartesianGridProps = {
  strokeDasharray: '3 3',
  stroke: chartTheme.gridColor,
  vertical: false,
};

export const defaultXAxisProps = {
  tick: { fontSize: chartTheme.fontSize, fill: chartTheme.tickColor },
  tickLine: false,
  axisLine: { stroke: chartTheme.gridColor },
};

export const defaultYAxisProps = {
  tick: { fontSize: chartTheme.fontSize, fill: chartTheme.tickColor },
  tickLine: false,
  axisLine: false,
  width: 60,
};

export const defaultTooltipProps = {
  contentStyle: {
    backgroundColor: chartTheme.tooltipBg,
    border: `1px solid ${chartTheme.tooltipBorder}`,
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: `${chartTheme.fontSize}px`,
    color: chartTheme.tooltipText,
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
  },
  cursor: { stroke: chartTheme.cursorStroke, strokeWidth: 1 },
};

export const defaultLegendProps = {
  wrapperStyle: {
    fontSize: `${chartTheme.fontSize}px`,
    paddingTop: '12px',
  },
};
